// Marking a notification read, on @bambi/api-client's catch-all split. Cache
// policy like the rest of cachePolicy.ts, but it writes to cached list
// entries, so it goes through the typed split rather than the by-name patch
// table there.

import { bambiApi } from '@bambi/api-client/bambi';
import type { Notification } from '@bambi/api-client/schemas';
import type { Draft } from '@reduxjs/toolkit';

/** The two cached lists a read notification can appear in. */
const NOTIFICATION_LISTS = [
  'notificationsNotificationsList',
  'notificationsAllNotificationsList',
] as const;

/**
 * Called by cachePolicy.ts before it wraps the definitions: the bridge
 * chains whatever onQueryStarted it finds, so this has to be there first.
 */
export const applyNotificationReadPatchPolicy = () =>
  bambiApi.enhanceEndpoints({
    endpoints: {
      notificationsNotificationsPartialUpdate: {
        // Don't invalidate the notifications tag — that triggers an immediate
        // refetch of the (unread-only) list, which drops the notification out
        // from under the user right as they mark it read. Instead, optimistically
        // patch it in every cached list so it stays visible until the next
        // natural refetch.
        invalidatesTags: [],
        onQueryStarted: async (
          { id, patchedNotificationWritable },
          { dispatch, getState, queryFulfilled }
        ) => {
          const patchNotification = (notification: Draft<Notification>) => {
            if (notification.id === id) {
              Object.assign(notification, patchedNotificationWritable);
            }
          };

          const patches = NOTIFICATION_LISTS.flatMap((endpointName) =>
            bambiApi.util
              .selectCachedArgsForQuery(getState(), endpointName)
              .map((args) =>
                dispatch(
                  bambiApi.util.updateQueryData(endpointName, args, (draft) => {
                    draft.results.forEach(patchNotification);
                  })
                )
              )
          );

          try {
            await queryFulfilled;
          } catch {
            patches.forEach((patch) => patch.undo());
          }
        },
      },
    },
  });
